export const OFFICE_OPEN_EXTENSIONS = [
  'doc',
  'docx',
  'xls',
  'xlsx',
  'xlsm',
  'ppt',
  'pptx',
] as const;
export const PREVIEW_OPEN_EXTENSIONS = [
  'png',
  'jpg',
  'jpeg',
  'gif',
  'webp',
  'bmp',
  'svg',
  'pdf',
] as const;
export const OPENABLE_FILE_EXTENSIONS = [
  ...OFFICE_OPEN_EXTENSIONS,
  ...PREVIEW_OPEN_EXTENSIONS,
] as const;

export function convertToFileName(f: string): string {
  return f.split('-').slice(4).join('-');
}

export function getComparableFileName(fileName: string): string {
  const parts = fileName.split('-');
  const hasGeneratedPrefix =
    parts.length > 4 &&
    /^\d+$/.test(parts[0] ?? '') &&
    /^\d+$/.test(parts[1] ?? '') &&
    /^\d+$/.test(parts[2] ?? '') &&
    /^\d+$/.test(parts[3] ?? '');

  if (hasGeneratedPrefix) {
    const derivedName = parts.slice(4).join('-');
    if (derivedName !== '') {
      return derivedName.toLowerCase();
    }
  }

  return fileName.toLowerCase();
}

// Stored names/URIs can contain a literal '%' (e.g. "100% plan.pdf"), which makes
// decodeURIComponent throw during render; fall back to the raw value.
export function safeDecodeURIComponent(value: string): string {
  try {
    return decodeURIComponent(value);
  } catch {
    return value;
  }
}

export function getFileExtension(fileNameOrUrl: string): string | null {
  const normalized = safeDecodeURIComponent(fileNameOrUrl).toLowerCase();
  if (!normalized.includes('.')) return null;
  return normalized.split('.').pop() ?? null;
}

export function getOfficeViewerUrl(fileUrl: string): string {
  try {
    const parsedUrl = new URL(fileUrl);
    const pathname = safeDecodeURIComponent(parsedUrl.pathname).toLowerCase();

    const queryFileNameCandidates = [
      parsedUrl.searchParams.get('filename'),
      parsedUrl.searchParams.get('fileName'),
      parsedUrl.searchParams.get('file'),
      parsedUrl.searchParams.get('name'),
      parsedUrl.searchParams.get('download'),
      parsedUrl.searchParams.get('attachment'),
    ]
      .filter((value): value is string => value != null && value !== '')
      .map((value) => safeDecodeURIComponent(value).toLowerCase());

    const candidateWithExtension = [pathname, ...queryFileNameCandidates].find((candidate) =>
      candidate.includes('.')
    );
    const extension = candidateWithExtension?.split('.').pop() ?? null;

    if (
      extension != null &&
      OFFICE_OPEN_EXTENSIONS.includes(extension as (typeof OFFICE_OPEN_EXTENSIONS)[number])
    ) {
      return `https://view.officeapps.live.com/op/embed.aspx?src=${encodeURIComponent(fileUrl)}`;
    }
  } catch {
    return fileUrl;
  }

  return fileUrl;
}
